import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {Box, Button, CircularProgress} from '@mui/material'
import React, {useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FieldEditorValidationContext} from '../../../../shared/order-editor/contexts/FieldEditorValidationContext'

import {ContinueWithMaterialVariationsButton} from '../../../../orderIntake/components/buttons/ContinueWithMaterialVariationsButton'
import {ResetMaterialVariationsButton} from '../../../../shared/order-editor/components/buttons/ResetMaterialVariationsButton'
import {useMaterialVariationsState} from '../../../../shared/order-editor/hooks/useMaterialVariationsState'
import {useOrderManagementFieldConfigurations} from '../../../hooks/useOrderManagementFieldConfigurations'
import {FieldEditorError} from '../shared/FieldEditorError'
import {FieldEditorHeader} from '../shared/FieldEditorHeader'
import {FieldEditorTitle} from '../shared/FieldEditorTitle'

export interface FieldEditorViewProps {
  fieldId: string
  onBack: () => void
  onSelectAndContinue?: () => void
  goToField?: (fieldId: string) => void
  isLoading?: boolean
  /** Additional external disabled flag (e.g. plusLoadQuantity === 0 in Plus Load flow) */
  isSelectAndContinueDisabled?: boolean
  // TODO: Add jobSite when available
  // jobSite?: OrderIntakeSite | null
}

/**
 * Hook to find field configuration by fieldId
 * Searches both top-level fields and subSteps
 */
const useFieldConfig = (fieldId: string) => {
  const fieldConfigurations = useOrderManagementFieldConfigurations()

  // Find the configuration for the current field (check top-level and subSteps)
  const fieldConfig = fieldConfigurations.find((config) => config.fieldId === fieldId)

  // If not found at top level, search in subSteps and track parent
  const parentConfig = !fieldConfig
    ? fieldConfigurations.find((config) =>
        config.subSteps?.some((subStep) => subStep.fieldId === fieldId)
      )
    : undefined

  const subStepConfig = parentConfig?.subSteps?.find((subStep) => subStep.fieldId === fieldId)

  const currentConfig = fieldConfig ?? subStepConfig
  const isSubStep = !!subStepConfig
  const parentFieldId = parentConfig?.fieldId

  // Get enabled subSteps for the current field (only for top-level fields)
  const enabledSubSteps = fieldConfig?.subSteps?.filter((subStep) => subStep.isEnabled) ?? []

  return {currentConfig, isSubStep, parentFieldId, enabledSubSteps}
}

/**
 * FieldEditorView Component
 *
 * Renders the editor component for a specific field based on its fieldId.
 * Includes header with back navigation, field title, and the editor component.
 *
 * This component looks up the field configuration and renders the
 * appropriate editor component for the selected field.
 */
export const FieldEditorView: React.FC<FieldEditorViewProps> = ({
  fieldId,
  onBack,
  onSelectAndContinue,
  goToField,
  isLoading = false,
  isSelectAndContinueDisabled = false
}) => {
  const {t} = useTranslation()
  const {selectedMaterial, selectedMaterialHasVariations, resetMaterialVariations} = useMix()
  const {shouldShowResetButton} = useMaterialVariationsState(fieldId)
  const {currentConfig, isSubStep, parentFieldId, enabledSubSteps} = useFieldConfig(fieldId)

  // If field not found, show error state
  if (!currentConfig) {
    return <FieldEditorError onBack={onBack} />
  }

  const {EditorComponent, label} = currentConfig

  // Compute display flags
  const hasEnabledSubSteps = enabledSubSteps.length > 0 && !!goToField
  const canConfigureMix = !!selectedMaterial && selectedMaterialHasVariations
  const shouldShowSubStepButtons = hasEnabledSubSteps && canConfigureMix

  // Check if current field is the material variations subStep
  const isMaterialVariationsSubStep = isSubStep && fieldId === 'materialVariations'

  // Navigation handlers - subSteps navigate to parent, top-level fields navigate to overview
  const handleBackOrDiscard = () => {
    // Reset material variations when going back from the variations subStep
    if (isMaterialVariationsSubStep) {
      resetMaterialVariations?.()
    }

    if (isSubStep && parentFieldId && goToField) {
      goToField(parentFieldId)
    } else {
      onBack()
    }
  }

  // Handler for Select and Continue button (always goes to overview)
  const handleSelectAndContinue = () => {
    if (onSelectAndContinue) {
      onSelectAndContinue()
      return
    }
    onBack()
  }

  // isFieldValid starts true for backward compatibility (other fields don't report validation)
  const [isFieldValid, setIsFieldValid] = useState(true)
  const handleSetFieldValid = useCallback((valid: boolean) => setIsFieldValid(valid), [])

  const canProceed = isFieldValid && !isSelectAndContinueDisabled

  return (
    <Box p={2} height="100%">
      <Box
        sx={{display: 'flex', flexDirection: 'column', gap: '32px'}}
        data-test-id={`field-editor-${fieldId}`}
      >
        {/* Header with back navigation */}
        <FieldEditorHeader onBack={handleBackOrDiscard} />

        {/* Field Title */}
        <FieldEditorTitle title={t(label)} />

        {/* Editor Component */}
        <Box sx={{flexGrow: 1}} data-test-id="field-editor-content">
          <FieldEditorValidationContext.Provider value={{setFieldValid: handleSetFieldValid}}>
            <EditorComponent />
          </FieldEditorValidationContext.Provider>
        </Box>

        {/* SubStep buttons - shown when parent field has enabled subSteps and material can be configured */}
        {shouldShowSubStepButtons &&
          enabledSubSteps.map((subStep) => (
            <ContinueWithMaterialVariationsButton
              key={subStep.fieldId}
              materialId={selectedMaterial?.materialId ?? undefined}
              onContinue={() => goToField?.(subStep.fieldId)}
              testId={`edit-${subStep.fieldId}-button`}
            />
          ))}

        {/* Action Buttons */}
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {/* Reset to default values button - only for material variations step with configured values */}
          {shouldShowResetButton && (
            <ResetMaterialVariationsButton
              onReset={() => resetMaterialVariations?.()}
              testId="reset-material-variations-field-editor-button"
              variant="fixed"
            />
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleSelectAndContinue}
            disabled={!canProceed || isLoading}
            sx={{
              height: '48px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              color: '#ffffff',
              backgroundColor: canProceed && !isLoading ? '#016ad4' : '#d1d9e0',
              fontFamily: 'Inter',
              '&:hover': {backgroundColor: canProceed && !isLoading ? '#014ba8' : '#d1d9e0'},
              '&:disabled': {backgroundColor: '#d1d9e0', color: '#888', cursor: 'not-allowed'}
            }}
            data-test-id="step-edit-select-continue-button"
            startIcon={isLoading ? <CircularProgress size={20} sx={{color: '#888'}} /> : undefined}
          >
            {t('orderIntake.rmc.stepper.buttons.selectAndContinue', 'Select & Continue')}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={handleBackOrDiscard}
            sx={{
              height: '48px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              color: '#da0901',
              borderColor: '#e8ecf0',
              backgroundColor: '#ffffff',
              fontFamily: 'Inter',
              '&:hover': {borderColor: '#da0901', backgroundColor: '#f7f9fa'}
            }}
            data-test-id="step-edit-discard-button"
          >
            {t('orderIntake.rmc.stepper.buttons.discard', 'Discard')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
